import _isEmpty from 'lodash/isEmpty';
import createActionHandlers from '@sift/resift/redux/FetchUtils/createActionHandlers';
import { mergeSuccess, mergeLoading, mergeError } from '@sift/resift/redux/FetchUtils/FetchUtils';

const FIELD_EXPANDS = ['fields', 'authorizationStatus', 'groups'];

/**
 * recursiveStripEmptyStrings
 * Clean up our form data so we're not sending empty strings to our data source
 * @param {Object} obj
 * @returns {Object} A new object without empty strings (clean up the data)
 */
function recursiveStripEmptyStrings(obj) {
  const newObject = { ...obj };

  for (const key in newObject) {
    if (newObject[key] === '' || newObject[key] === null) {
      newObject[key] = null;
    } else if (!Array.isArray(newObject[key]) && typeof newObject[key] === 'object') {
      if (_isEmpty(newObject[key])) {
        newObject[key] = null;
      } else {
        newObject[key] = recursiveStripEmptyStrings(newObject[key]);
      }
    }
  }

  return newObject;
}

export const SOURCE_CREATE_IDENTIFIER = 'SOURCE_CREATE_IDENTIFIER';

export const SOURCE_CREATE = 'SOURCE_CREATE';
export const SOURCE_CREATE_SUCCESS = 'SOURCE_CREATE_SUCCESS';
export const SOURCE_CREATE_ERROR = 'SOURCE_CREATE_ERROR';

export const SOURCE_INIT = 'SOURCE_INIT';
export const SOURCE_INIT_SUCCESS = 'SOURCE_INIT_SUCCESS';
export const SOURCE_INIT_ERROR = 'SOURCE_INIT_ERROR';

export const SOURCE_FETCH = 'SOURCE_FETCH';
export const SOURCE_FETCH_SUCCESS = 'SOURCE_FETCH_SUCCESS';
export const SOURCE_FETCH_ERROR = 'SOURCE_FETCH_ERROR';

export const SOURCE_SAVE = 'SOURCE_SAVE';
export const SOURCE_SAVE_SUCCESS = 'SOURCE_SAVE_SUCCESS';
export const SOURCE_SAVE_ERROR = 'SOURCE_SAVE_ERROR';

export const SOURCE_FIELDS_SAVE = 'SOURCE_FIELDS_SAVE';
export const SOURCE_FIELDS_SAVE_SUCCESS = 'SOURCE_FIELDS_SAVE_SUCCESS';
export const SOURCE_FIELDS_SAVE_ERROR = 'SOURCE_FIELDS_SAVE_ERROR';

export const SOURCE_DELETE = 'SOURCE_DELETE';
export const SOURCE_DELETE_SUCCESS = 'SOURCE_DELETE_SUCCESS';
export const SOURCE_DELETE_ERROR = 'SOURCE_DELETE_ERROR';

export const SOURCE_PUBLIC_KEY_SAVE = 'SOURCE_PUBLIC_KEY_SAVE';
export const SOURCE_PUBLIC_KEY_SAVE_SUCCESS = 'SOURCE_PUBLIC_KEY_SAVE_SUCCESS';
export const SOURCE_PUBLIC_KEY_SAVE_ERROR = 'SOURCE_PUBLIC_KEY_SAVE_ERROR';

export const SIGNED_UPLOAD_FETCH = 'SIGNED_UPLOAD_FETCH';
export const SIGNED_UPLOAD_FETCH_SUCCESS = 'SIGNED_UPLOAD_FETCH_SUCCESS';
export const SIGNED_UPLOAD_FETCH_ERROR = 'SIGNED_UPLOAD_FETCH_ERROR';

export const SOURCE_FILE_CREATE = 'SOURCE_FILE_CREATE';
export const SOURCE_FILE_CREATE_SUCCESS = 'SOURCE_FILE_CREATE_SUCCESS';
export const SOURCE_FILE_CREATE_ERROR = 'SOURCE_FILE_CREATE_ERROR';

export const SOURCE_FILE_HEADINGS_FETCH = 'SOURCE_FILE_HEADINGS_FETCH';
export const SOURCE_FILE_HEADINGS_FETCH_SUCCESS = 'SOURCE_FILE_HEADINGS_FETCH_SUCCESS';
export const SOURCE_FILE_HEADINGS_FETCH_ERROR = 'SOURCE_FILE_HEADINGS_FETCH_ERROR';

export const SOURCE_SYNC_ENABLE = 'SOURCE_SYNC_ENABLE';
export const SOURCE_SYNC_ENABLE_SUCCESS = 'SOURCE_SYNC_ENABLE_SUCCESS';
export const SOURCE_SYNC_ENABLE_ERROR = 'SOURCE_SYNC_ENABLE_ERROR';

export const SOURCE_SYNC_QUEUE = 'SOURCE_SYNC_QUEUE';
export const SOURCE_SYNC_QUEUE_SUCCESS = 'SOURCE_SYNC_QUEUE_SUCCESS';
export const SOURCE_SYNC_QUEUE_ERROR = 'SOURCE_SYNC_QUEUE_ERROR';

export const SOURCE_PRIMARY_MAPPING_UPDATE = 'SOURCE_PRIMARY_MAPPING_UPDATE';
export const SOURCE_PRIMARY_MAPPING_UPDATE_SUCCESS = 'SOURCE_PRIMARY_MAPPING_UPDATE_SUCCESS';
export const SOURCE_PRIMARY_MAPPING_UPDATE_ERROR = 'SOURCE_PRIMARY_MAPPING_UPDATE_ERROR';

export const SOURCE_MAPPINGS_SAVE = 'SOURCE_MAPPINGS_SAVE';
export const SOURCE_MAPPINGS_SAVE_SUCCESS = 'SOURCE_MAPPINGS_SAVE_SUCCESS';
export const SOURCE_MAPPINGS_SAVE_ERROR = 'SOURCE_MAPPINGS_SAVE_ERROR';

export const SOURCE_FILE_MAPPINGS_FETCH = 'SOURCE_FILE_MAPPINGS_FETCH';
export const SOURCE_FILE_MAPPINGS_FETCH_SUCCESS = 'SOURCE_FILE_MAPPINGS_FETCH_SUCCESS';
export const SOURCE_FILE_MAPPINGS_FETCH_ERROR = 'SOURCE_FILE_MAPPINGS_FETCH_ERROR';

export function initializeSource(sourceId) {
  return {
    type: SOURCE_INIT,
    identifier: `${sourceId}-initialize`,
    payload: {
      id: sourceId,
      data: {},
    },
  };
}

export function createSource(directoryId, type, label, isPrimary) {
  return {
    type: SOURCE_CREATE,
    identifier: SOURCE_CREATE_IDENTIFIER,
    payload: {
      data: {
        type,
        directoryId,
        label,
        isPrimary,
      },
    },
  };
}

export function fetchSource(sourceId, expand = FIELD_EXPANDS) {
  return {
    type: SOURCE_FETCH,
    identifier: sourceId,
    payload: {
      sourceId,
      query: {
        expand,
      },
    },
  };
}

export function saveSource(source, expand = FIELD_EXPANDS) {
  const sourceDataWithoutEmptyStrings = recursiveStripEmptyStrings(source);

  return {
    type: SOURCE_SAVE,
    identifier: source.id,
    payload: {
      id: source.id,
      data: sourceDataWithoutEmptyStrings,
      query: {
        expand,
      },
    },
  };
}

export function saveSourceFields(sourceId, updatedFields) {
  return {
    type: SOURCE_FIELDS_SAVE,
    identifier: `${sourceId}-save-fields`,
    payload: {
      id: sourceId,
      data: updatedFields,
    },
  };
}

export function deleteSource(sourceId, expand = FIELD_EXPANDS) {
  return {
    type: SOURCE_DELETE,
    identifier: sourceId,
    payload: {
      id: sourceId,
      query: {
        expand,
      },
    },
  };
}

// Used with SFTP
export function saveSourcePublicKey(sourceId, publicKey, expand = FIELD_EXPANDS) {
  return {
    type: SOURCE_PUBLIC_KEY_SAVE,
    identifier: `${sourceId}-save-public-key`,
    payload: {
      id: sourceId,
      data: {
        publicKey,
      },
      query: {
        expand,
      },
    },
  };
}

export const fetchSignedUpload = query => {
  return {
    type: SIGNED_UPLOAD_FETCH,
    identifier: 'signedUpload',
    payload: {
      query,
    },
  };
};

export const createSourceFile = sourceFile => {
  return {
    type: SOURCE_FILE_CREATE,
    identifier: 'sourceFile',
    payload: {
      data: sourceFile,
    },
  };
};

export const fetchSourceFileHeadings = sourceFileId => {
  return {
    type: SOURCE_FILE_HEADINGS_FETCH,
    identifier: `${sourceFileId}-sourceFileHeadings`,
    payload: {
      id: sourceFileId,
    },
  };
};

export const setSourceSyncEnabled = (sourceId, isEnabled = false) => {
  return {
    type: SOURCE_SYNC_ENABLE,
    identifier: `${sourceId}-setSourceSyncEnabled`,
    payload: {
      sourceId,
      data: {
        syncEnabled: isEnabled,
      },
    },
  };
};

export const queueSourceSync = (sourceId, sourceFileId) => {
  const dataKey = sourceFileId
    ? {
        sourceFile: {
          id: sourceFileId,
        },
      }
    : undefined;

  return {
    type: SOURCE_SYNC_QUEUE,
    identifier: `${sourceId}-queueSourceSync`,
    payload: {
      sourceId,
      data: dataKey,
    },
  };
};

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  ...createActionHandlers(SOURCE_CREATE),
  ...createActionHandlers(SOURCE_INIT),
  ...createActionHandlers(SOURCE_FETCH),
  SOURCE_SAVE: (state, action) => {
    const newState = mergeLoading(state, action);
    return {
      ...newState,
      sourceSaveStatus: 2,
    };
  },
  SOURCE_SAVE_SUCCESS: (state, action) => {
    const newState = mergeSuccess(state, action);
    return {
      ...newState,
      sourceSaveStatus: 1,
    };
  },
  SOURCE_SAVE_ERROR: (state, action) => {
    const newState = mergeError(state, action);
    return {
      ...newState,
      sourceSaveStatus: 3,
    };
  },
  ...createActionHandlers(SOURCE_DELETE, true),
  ...createActionHandlers(SOURCE_PUBLIC_KEY_SAVE),
  ...createActionHandlers(SOURCE_SYNC_ENABLE),
  ...createActionHandlers(SOURCE_SYNC_QUEUE),
  ...createActionHandlers(SOURCE_PRIMARY_MAPPING_UPDATE),
  ...createActionHandlers(SOURCE_FILE_HEADINGS_FETCH),
  ...createActionHandlers(SIGNED_UPLOAD_FETCH),
  ...createActionHandlers(SOURCE_FILE_CREATE),
  ...createActionHandlers(SOURCE_MAPPINGS_SAVE),
  ...createActionHandlers(SOURCE_FILE_MAPPINGS_FETCH, false),
  ...createActionHandlers(SOURCE_SYNC_ENABLE),
  ...createActionHandlers(SOURCE_FIELDS_SAVE),
  SOURCE_SYNC_ENABLE: (state, action) => {
    const newState = mergeLoading(state, action);
    return {
      ...newState,
      sourceSyncStatus: 2,
    };
  },

  SOURCE_SYNC_ENABLE_SUCCESS: (state, action) => {
    const newSource = action.payload.data;
    return {
      ...state,
      sourceSyncStatus: 1,
      objects: {
        ...state.objects,
        [newSource.id]: {
          ...state.objects[newSource.id],
          syncEnabled: newSource.syncEnabled,
        },
      },
    };
  },
  SOURCE_SYNC_ENABLE_ERROR: (state, action) => {
    const newState = mergeError(state, action);
    return {
      ...newState,
      sourceSyncStatus: 3,
    };
  },
};

// ------------------------------------
// Reducer
// ------------------------------------

export const initialState = {
  fetches: {},
  objects: {},
  sourceSaveStatus: 1, // sourceSave conflicts with the withSource loadingState. This is needed to properly show loading states after editing source settings
  sourceSyncStatus: 1, // sourceSync conflicts with the withSource loadingState. This is needed to properly show loading states after editing source settings
};

export default function SourcesReducer(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type];

  return handler ? handler(state, action) : state;
}
